import React, { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { db, collection, addDoc } from "../firebase";

/* =====================================================
   Xeroo · Cotizador Conversacional (Scroll)
   - Logo + branding + logo cliente (ancho para marcas grandes)
   - Cliente desde URL /propuesta/:slug
   - Servicios ampliados (Automatizaciones + Add-ons)
   - Resumen con totales (mensual vs únicos) + comentario de orden
   - Financiación de implementación (1/3/6/12) con recargo
   - Envío por mail con popup + success modal
   - Registro en "leadsNew"
   - Reglas: Consultoría (1), WhatsApp (uno u otro), Integraciones (sin/ERP/Ecommerce multi)
   ===================================================== */

const peso = (n) =>
    new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
    }).format(n || 0);

const OUR_EMAIL = "hola@xeroo.ai";

export default function XerooConversationalLanding() {
    // Estado principal
    const [selected, setSelected] = useState({}); // {serviceId: 1}
    const [view, setView] = useState("flow"); // flow | cases
    const [clientName, setClientName] = useState("");
    const [clientLogo, setClientLogo] = useState("/xeroo-white.svg");
    const [isBigBrand, setIsBigBrand] = useState(false); // logos más anchos
    const [orderNotes, setOrderNotes] = useState("");

    // Financiación
    const [financingMonths, setFinancingMonths] = useState(1); // 1,3,6,12
    const financingRates = { 1: 0, 3: 0.05, 6: 0.09, 12: 0.18 };

    // Email modal & success modal
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [showSuccess, setShowSuccess] = useState(false);

    // Detectar cliente desde URL: /propuesta/:slug y logo
    useEffect(() => {
        try {
            const path = window.location.pathname || "";
            const match = path.match(/\/(?:propuesta|proposal)\/([^/]+)/i);
            if (match && match[1]) {
                const slug = decodeURIComponent(match[1]).toLowerCase();
                const pretty = slug
                    .replace(/[-_]+/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase());
                setClientName(pretty.trim());
                const logoMap = {
                    tarquini: "/tarquini.png",
                    plastiferro: "/plastiferro.png",
                    fresh: "/fresh.png",
                };
                // Detecta coincidencias parciales
                const key = Object.keys(logoMap).find((k) => slug.includes(k));
                if (key) {
                    setClientLogo(logoMap[key]);
                    setIsBigBrand(true); // logos de marcas grandes, más ancho
                }
            }
        } catch { }
    }, []);

    // Descuentos por bundle
    const bundleDiscount = (count) => (count >= 5 ? 15 : count >= 3 ? 10 : 0);

    // Cálculos base
    const pickedServices = useMemo(
        () => servicesData.filter((s) => selected[s.id]),
        [selected]
    );
    const monthly = useMemo(
        () => pickedServices.reduce((acc, s) => acc + (s.priceMonthly || 0), 0),
        [pickedServices]
    );
    const setup = useMemo(
        () => pickedServices.reduce((acc, s) => acc + (s.priceSetup || 0), 0),
        [pickedServices]
    );
    const discountPct = useMemo(
        () => bundleDiscount(pickedServices.length),
        [pickedServices]
    );
    const discountAmount = useMemo(
        () => Math.round((monthly * discountPct) / 100),
        [monthly, discountPct]
    );
    const monthlyAfterDiscount = useMemo(
        () => monthly - discountAmount,
        [monthly, discountAmount]
    );

    // Financiación (prorratea setup y aplica recargo)
    const financedMonthly = useMemo(() => {
        if (financingMonths === 1 || setup <= 0) return 0;
        const rate = financingRates[financingMonths] ?? 0;
        const totalFinanced = setup * (1 + rate);
        return Math.ceil(totalFinanced / financingMonths);
    }, [setup, financingMonths]);

    // Total mensual final
    const grandMonthly = useMemo(
        () => monthlyAfterDiscount + financedMonthly,
        [monthlyAfterDiscount, financedMonthly]
    );

    // Reglas de selección (exclusividades y dependencias)
    const toggle = (id) => {
        const svc = servicesData.find((s) => s.id === id);
        if (!svc) return;

        setSelected((prev) => {
            const next = { ...prev };
            const currently = !!next[id];

            // Exclusivos por grupo (consultoría / whatsapp)
            if (svc.exclusiveGroup) {
                servicesData
                    .filter((s) => s.exclusiveGroup === svc.exclusiveGroup)
                    .forEach((s) => (next[s.id] = 0));
                next[id] = currently ? 0 : 1;
                return next;
            }

            // Integraciones: "sin integracion" desmarca otras; elegir otras desmarca "sin integracion"
            if (svc.category === "Integraciones") {
                if (svc.id === "int_none") {
                    servicesData
                        .filter((s) => s.category === "Integraciones" && s.id !== "int_none")
                        .forEach((s) => (next[s.id] = 0));
                    next[svc.id] = currently ? 0 : 1;
                    return next;
                } else {
                    next["int_none"] = 0; // desmarca "sin integración"
                }
            }

            // Toggle normal
            next[id] = currently ? 0 : 1;
            return next;
        });
    };

    const clientSuffix = clientName ? ` de ${clientName}` : "";

    // Validación simple email
    const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

    // Abrir modal de email
    const openEmailModal = () => {
        setEmailError("");
        setShowEmailModal(true);
    };

    // Enviar propuesta por mail + guardar lead
    const handleSendProposal = async (targetEmail) => {
        const payload = {
            createdAt: new Date().toISOString(),
            clientName: clientName || null,
            clientEmail: targetEmail,
            items: pickedServices.map((s) => ({
                id: s.id,
                name: s.name,
                monthly: s.priceMonthly,
                setup: s.priceSetup,
                category: s.category,
            })),
            subtotalMonthly: monthly,
            discountPct,
            discountAmount,
            monthlyAfterDiscount,
            setup,
            financedMonths: financingMonths,
            financedMonthly,
            grandMonthly, // mensual final con financiación incluida
            notes: orderNotes,
            sendTo: [targetEmail, OUR_EMAIL],
        };

        try {
            // ✅ Guardar SIEMPRE en Firestore usando tu patrón (colección newClientsIA)
            await addDoc(collection(db, "newClientsIA"), {
                ...payload,
                createdAtTS: Date.now(), // marca de tiempo de Firestore
                status: "sent",
                source: "cotizador-xeroo",
                clientPath: window.location.pathname || null,
                clientLogo: clientLogo || null,
            });
            setShowEmailModal(false);
            setShowSuccess(true); // modal de éxito
        } catch (e) {
            console.error(e);
            setEmailError("No se pudo enviar la propuesta. Probá de nuevo.");
        }
    };


    // Submit del modal de email
    const onEmailSubmit = (e) => {
        e.preventDefault();
        if (!isValidEmail(email)) {
            setEmailError("Ingresá un mail válido");
            return;
        }
        handleSendProposal(email);
    };

    return (
        <div className="min-h-screen bg-neutral-50 text-neutral-900">
            {/* HERO */}
            <section className="bg-gradient-to-r from-[#575fd5] to-[#7f87ff] text-white py-16">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="flex items-center gap-4 mb-6">
                        <img
                            src="/xeroo-white.svg"
                            alt="Xeroo"
                            className="h-8 w-auto"
                            onError={(e) => (e.currentTarget.style.display = "none")}
                        />
                        <span className="text-white/90 text-sm">IA + Automatizaciones</span>
                        {/* Logo cliente si corresponde (más ancho para marcas grandes) */}
                        {clientName && (
                            <div className="ml-auto flex items-center gap-2">
                                <span className="text-white/70 text-xs hidden sm:inline">
                                    Propuesta para
                                </span>
                                <img
                                    src={clientLogo}
                                    alt={clientName}
                                    className={isBigBrand ? "h-14 w-auto max-w-[220px]" : "h-12 w-auto"}
                                    style={{ objectFit: "contain" }}
                                    onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold">
                        Vamos a adaptar el servicio a tu negocio
                    </h1>
                    <p className="text-lg md:text-xl mt-3 opacity-95">
                        Elegí paso a paso lo que necesitás. Te mostramos la cotización en vivo.
                    </p>
                    <div className="mt-6 flex gap-3">
                        <button
                            onClick={() => setView("flow")}
                            className="px-5 py-3 rounded-xl bg-white text-[#575fd5] font-semibold"
                        >
                            Armar mi propuesta
                        </button>
                        <button
                            onClick={() => setView("cases")}
                            className="px-5 py-3 rounded-xl border border-white font-semibold hover:bg-white hover:text-[#575fd5]"
                        >
                            Ver casos reales
                        </button>
                    </div>
                </div>
            </section>

            {/* CONTENT */}
            <div className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Conversational flow */}
                <main className="lg:col-span-2">
                    {view === "flow" ? (
                        <div className="space-y-10">
                            {flowSections(clientSuffix).map((sec) => (
                                <ConversationBlock
                                    key={sec.id}
                                    section={sec}
                                    selected={selected}
                                    toggle={toggle}
                                />
                            ))}

                            {/* Automatizaciones (chips seleccionables) */}
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                className="rounded-2xl bg-white border p-5 shadow-sm"
                            >
                                <h3 className="text-xl font-semibold">
                                    ¿Querés sumar automatizaciones?
                                </h3>
                                <p className="text-sm text-neutral-600 mt-1">
                                    Acciones automáticas que impactan ventas y operaciones.
                                </p>
                                <div className="mt-4 flex flex-wrap gap-2">
                                    {servicesData
                                        .filter((s) => s.category === "Automatizaciones")
                                        .map((s) => (
                                            <button
                                                key={s.id}
                                                onClick={() => toggle(s.id)}
                                                className={`text-sm px-3 py-2 rounded-full border ${selected[s.id]
                                                    ? "bg-[#575fd5] text-white border-[#575fd5]"
                                                    : "hover:bg-neutral-100"
                                                    }`}
                                            >
                                                {s.name} · {peso(s.priceMonthly)}/m
                                                {s.priceSetup ? ` + ${peso(s.priceSetup)} setup` : ""}
                                            </button>
                                        ))}
                                </div>
                            </motion.div>

                            {/* Add-ons (chips seleccionables) */}
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                className="rounded-2xl bg-white border p-5 shadow-sm"
                            >
                                <h3 className="text-xl font-semibold">¿Querés sumar add-ons?</h3>
                                <p className="text-sm text-neutral-600 mt-1">
                                    Mejoras opcionales para potenciar tu implementación.
                                </p>
                                <div className="mt-4 flex flex-wrap gap-2">
                                    {servicesData
                                        .filter((s) => s.category === "Add-ons")
                                        .map((s) => (
                                            <button
                                                key={s.id}
                                                onClick={() => toggle(s.id)}
                                                className={`text-sm px-3 py-2 rounded-full border ${selected[s.id]
                                                    ? "bg-[#575fd5] text-white border-[#575fd5]"
                                                    : "hover:bg-neutral-100"
                                                    }`}
                                            >
                                                {s.name} · {peso(s.priceMonthly)}/m
                                                {s.priceSetup ? ` + ${peso(s.priceSetup)} setup` : ""}
                                            </button>
                                        ))}
                                </div>
                            </motion.div>
                        </div>
                    ) : (
                        <CasesView />
                    )}
                </main>

                {/* Preview sticky */}
                <aside className="lg:col-span-1">
                    <div className="sticky top-24 rounded-2xl bg-white border p-5 shadow-sm">
                        <h3 className="text-lg font-semibold">Tu propuesta</h3>
                        <p className="text-xs text-neutral-500">
                            Se actualiza con cada selección.
                        </p>

                        <ul className="mt-3 max-h-64 overflow-auto pr-1 text-sm">
                            {pickedServices.length ? (
                                pickedServices.map((s) => (
                                    <li
                                        key={s.id}
                                        className="flex items-start justify-between gap-2 py-2 border-b last:border-0"
                                    >
                                        <div>
                                            <div className="font-medium leading-tight">{s.name}</div>
                                            <div className="text-xs text-neutral-500">{s.category}</div>
                                        </div>
                                        <div className="text-right">
                                            {!!s.priceSetup && (
                                                <div className="text-[11px] text-neutral-500">
                                                    Setup {peso(s.priceSetup)}
                                                </div>
                                            )}
                                            <div className="font-semibold">
                                                {peso(s.priceMonthly)}/m
                                            </div>
                                        </div>
                                    </li>
                                ))
                            ) : (
                                <li className="text-neutral-400">
                                    No agregaste servicios todavía.
                                </li>
                            )}
                        </ul>

                        {/* Totales + financiación */}
                        <div className="mt-4 space-y-1 text-sm">
                            <div className="flex justify-between">
                                <span>Subtotal mensual</span>
                                <span>{peso(monthly)}</span>
                            </div>
                            {discountPct > 0 && (
                                <div className="flex justify-between text-emerald-700">
                                    <span>Descuento ({discountPct}%)</span>
                                    <span>-{peso(discountAmount)}</span>
                                </div>
                            )}
                            {financedMonthly > 0 && (
                                <div className="flex justify-between text-amber-700">
                                    <span>Implementación financiada ({financingMonths} meses)</span>
                                    <span>+{peso(financedMonthly)}/m</span>
                                </div>
                            )}
                            <div className="flex justify-between font-semibold">
                                <span>Total mensual</span>
                                <span>{peso(grandMonthly)}</span>
                            </div>
                            {setup > 0 && financingMonths === 1 && (
                                <div className="flex justify-between">
                                    <span className="text-xs text-neutral-500">
                                        Cargo único (implementación)
                                    </span>
                                    <span className="text-sm">{peso(setup)}</span>
                                </div>
                            )}
                            <div className="pt-2 text-[11px] text-neutral-500">
                                * Los valores no incluyen impuestos ni retenciones.
                            </div>
                        </div>

                        {/* Financiación */}
                        <div className="mt-4 grid grid-cols-1 gap-3">
                            <div>
                                <label className="text-xs text-neutral-600">
                                    Financiación de implementación
                                </label>
                                <select
                                    value={financingMonths}
                                    onChange={(e) => setFinancingMonths(parseInt(e.target.value))}
                                    className="mt-1 w-full rounded-xl border px-3 py-2"
                                >
                                    <option value={1}>1 pago (sin interés)</option>
                                    <option value={3}>3 meses (+5%)</option>
                                    <option value={6}>6 meses (+9%)</option>
                                    <option value={12}>12 meses (+18%)</option>
                                </select>
                            </div>
                        </div>

                        {/* Comentarios */}
                        <div className="mt-4">
                            <label className="text-xs text-neutral-600">
                                Comentarios adicionales
                            </label>
                            <textarea
                                className="mt-1 w-full rounded-xl border px-3 py-2 text-sm"
                                rows={3}
                                placeholder="Notas, particularidades de la implementación, horarios, etc."
                                value={orderNotes}
                                onChange={(e) => setOrderNotes(e.target.value)}
                            />
                        </div>

                        {/* CTA principal */}
                        <button
                            onClick={openEmailModal}
                            className="mt-4 w-full px-4 py-2 rounded-xl bg-[#575fd5] text-white"
                        >
                            Enviar propuesta por mail
                        </button>
                    </div>
                </aside>
            </div>

            {/* Footer */}
            <footer className="bg-neutral-100 mt-12 py-8">
                <div className="max-w-6xl mx-auto px-4 text-sm text-neutral-500 text-center">
                    © 2025 Xeroo · IA + Automatizaciones —{" "}
                    <a href="https://www.xeroo.ai" className="underline">
                        www.xeroo.ai
                    </a>
                </div>
            </footer>

            {/* Modales */}
            {showEmailModal && (
                <Modal onClose={() => setShowEmailModal(false)} title="Enviar propuesta por mail">
                    <form onSubmit={onEmailSubmit} className="space-y-3">
                        <div>
                            <label className="text-xs text-neutral-600">Email destino</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setEmailError("");
                                }}
                                placeholder="cliente@empresa.com"
                                className="mt-1 w-full rounded-xl border px-3 py-2"
                                required
                            />
                            {emailError && (
                                <div className="text-[12px] text-red-600 mt-1">{emailError}</div>
                            )}
                            {/* <div className="text-[11px] text-neutral-500 mt-2">
                                También enviaremos una copia a {OUR_EMAIL}.
                            </div> */}
                        </div>
                        <div className="flex gap-2 justify-end pt-2">
                            <button
                                type="button"
                                onClick={() => setShowEmailModal(false)}
                                className="px-4 py-2 rounded-xl border"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 rounded-xl bg-[#575fd5] text-white"
                            >
                                Enviar
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {showSuccess && (
                <Modal onClose={() => setShowSuccess(false)} title="¡Propuesta enviada!">
                    <div className="space-y-3">
                        <p className="text-sm">
                            Enviamos la propuesta al correo indicado y registramos el lead en
                            nuestra base. Te vamos a contactar a la brevedad.
                        </p>
                        <div className="flex justify-end">
                            <button
                                className="px-4 py-2 rounded-xl bg-[#575fd5] text-white"
                                onClick={() => setShowSuccess(false)}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Print minimiza distracciones */}
            <style>{`
        @media print { header, footer, .sticky { display: none !important; } }
      `}</style>
        </div>
    );
}

/* =====================
   Bloque conversacional
   ===================== */
function ConversationBlock({ section, selected, toggle }) {
    return (
        <motion.section
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-2xl bg-white border p-5 shadow-sm"
        >
            <div className="flex items-start gap-3">
                <div className="text-2xl">🤖</div>
                <div>
                    <h3 className="text-xl font-semibold">{section.title}</h3>
                    <p className="text-sm text-neutral-600 mt-1">{section.subtitle}</p>
                </div>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {section.options.map((opt) => (
                    <button
                        key={opt.id}
                        onClick={() => (opt.serviceId ? toggle(opt.serviceId) : null)}
                        className={`text-left px-4 py-3 rounded-xl border transition ${opt.serviceId && selected[opt.serviceId]
                            ? "bg-[#575fd5] text-white border-[#575fd5]"
                            : "hover:bg-neutral-50"
                            }`}
                    >
                        <div className="font-medium">{opt.label}</div>
                        {opt.desc && <div className="text-xs opacity-70">{opt.desc}</div>}
                        {opt.serviceId && (
                            <div className="text-xs mt-1 opacity-90">
                                {displayPrice(opt.serviceId)}
                            </div>
                        )}
                    </button>
                ))}
            </div>
        </motion.section>
    );
}

function displayPrice(serviceId) {
    const svc = servicesData.find((s) => s.id === serviceId);
    if (!svc) return "";
    const s = [];
    if (svc.priceMonthly) s.push(`${peso(svc.priceMonthly)}/mes`);
    if (svc.priceSetup) s.push(`+ ${peso(svc.priceSetup)} setup`);
    return s.join(" ");
}

/* =====================
   Modal simple (sin libs externas)
   ===================== */
function Modal({ children, onClose, title }) {
    return (
        <div className="fixed inset-0 z-50">
            <div
                className="absolute inset-0 bg-black/40"
                onClick={onClose}
                aria-hidden
            />
            <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold">{title}</h3>
                        <button onClick={onClose} className="text-neutral-500 text-xl leading-none">
                            ×
                        </button>
                    </div>
                    <div className="mt-3">{children}</div>
                </div>
            </div>
        </div>
    );
}

/* =====================
   Definición de secciones conversacionales
   (con títulos dinámicos usando sufijo del cliente)
   ===================== */
const flowSections = (CLIENT_SUFFIX = "") => [
    {
        id: "consultoria",
        title: "¿Necesitás el servicio de consultoría?",
        subtitle:
            "Para qué sirve: identificar oportunidades, priorizar y armar un roadmap accionable.",
        options: [
            {
                id: "c_no",
                label: "No necesito, ya sé qué quiero automatizar",
                desc: "Podés seguir con asistentes, integraciones o add-ons.",
            },
            {
                id: "c_starter",
                label: "Consultoría Starter",
                desc: "Reuniones mensuales + roadmap básico.",
                serviceId: "consultoria_starter",
            },
            {
                id: "c_premium",
                label: "Consultoría Premium",
                desc: "PM de automatizaciones, QA y reporting ejecutivo.",
                serviceId: "consultoria_premium",
            },
            {
                id: "c_enterprise",
                label: "Consultoría Enterprise",
                desc: "Célula dedicada, OKRs trimestrales y governance.",
                serviceId: "consultoria_enterprise",
            },
        ],
    },
    {
        id: "asistentes",
        title: `¿A quién está dirigido el asistente virtual${CLIENT_SUFFIX}?`,
        subtitle:
            "Definí el foco del asistente para respuestas, compras y tareas repetitivas.",
        options: [
            {
                id: "a_clientes",
                label: "Asistente para clientes finales",
                desc: "FAQs, estado de pedidos, cambios y devoluciones.",
                serviceId: "asistente_clientes",
            },
            {
                id: "a_mayoristas",
                label: "Asistente mayoristas / distribuidores",
                desc: "Stock, novedades y pedidos por WhatsApp.",
                serviceId: "asistente_mayoristas",
            },
            {
                id: "a_interno",
                label: "Asistente interno",
                desc: "SOPs, consultas operativas y datos (stock/ventas/contable).",
                serviceId: "asistente_interno",
            },
        ],
    },
    {
        id: "whatsapp",
        title: "¿Cómo querés operar WhatsApp Business?",
        subtitle: "Elegí el alcance de tus conversaciones (seleccioná uno).",
        options: [
            {
                id: "w_inbound",
                label: "Básico: solo conversaciones entrantes",
                desc: "Atención a mensajes que llegan a tu línea.",
                serviceId: "wa_entrantes",
            },
            {
                id: "w_bidi",
                label: "Avanzado: entrantes + salientes",
                desc: "Campañas, recordatorios, carritos y cobranzas.",
                serviceId: "wa_entrantes_salientes",
            },
        ],
    },
    {
        id: "integraciones",
        title: "¿Necesitás integraciones con tus sistemas?",
        subtitle:
            "Elegí sin integración o conectá ERP/Ecommerce (podés combinar Ecommerce).",
        options: [
            {
                id: "i_none",
                label: "Sin integración",
                desc: "Operación básica sin conectores.",
                serviceId: "int_none",
            },
            {
                id: "i_erp",
                label: "Integración con ERP",
                desc: "Stock, precios, clientes, pedidos.",
                serviceId: "int_erp",
            },
            {
                id: "i_shopify",
                label: "Ecommerce: Shopify/TiendaNube/Mercado Libre",
                desc: "Sync catálogo, stock y órdenes.",
                serviceId: "int_ecommerce",
            },
        ],
    },
];

/* =====================
   Catálogo de Servicios (ampliado)
   ===================== */
const servicesData = [
    // Consultoría (exclusivo uno de tres)
    {
        id: "consultoria_starter",
        category: "Consultoría",
        name: "Consultoría Starter",
        description: "Reuniones mensuales + roadmap básico.",
        priceMonthly: 400,
        priceSetup: 0,
        exclusiveGroup: "consultoria",
    },
    {
        id: "consultoria_premium",
        category: "Consultoría",
        name: "Consultoría Premium",
        description: "PM de automatizaciones, QA y reporting ejecutivo.",
        priceMonthly: 700,
        priceSetup: 0,
        exclusiveGroup: "consultoria",
    },
    {
        id: "consultoria_enterprise",
        category: "Consultoría",
        name: "Consultoría Enterprise",
        description: "Célula dedicada, OKRs y governance.",
        priceMonthly: 1200,
        priceSetup: 0,
        exclusiveGroup: "consultoria",
    },

    // Asistentes (multi)
    {
        id: "asistente_clientes",
        category: "Asistentes",
        name: "Asistente para clientes",
        description: "FAQs, estado de pedidos y devoluciones.",
        priceMonthly: 350,
        priceSetup: 700,
    },
    {
        id: "asistente_mayoristas",
        category: "Asistentes",
        name: "Asistente mayoristas/distribuidores",
        description: "Stock, novedades y pedidos por WhatsApp.",
        priceMonthly: 420,
        priceSetup: 900,
    },
    {
        id: "asistente_interno",
        category: "Asistentes",
        name: "Asistente interno",
        description: "SOPs y consultas internas (stock/ventas/contable).",
        priceMonthly: 480,
        priceSetup: 900,
    },

    // WhatsApp (exclusivo uno u otro)
    {
        id: "wa_entrantes",
        category: "Canales",
        name: "WhatsApp Básico (solo entrantes)",
        description: "Atiende consultas recibidas.",
        priceMonthly: 180,
        priceSetup: 300,
        exclusiveGroup: "whatsapp",
    },
    {
        id: "wa_entrantes_salientes",
        category: "Canales",
        name: "WhatsApp Avanzado (entrantes+salientes)",
        description: "Campañas, recordatorios, carritos y cobranzas.",
        priceMonthly: 280,
        priceSetup: 500,
        exclusiveGroup: "whatsapp",
    },

    // Integraciones
    { id: "int_none", category: "Integraciones", name: "Sin integración", description: "Operación básica sin conectores.", priceMonthly: 0, priceSetup: 0 },
    { id: "int_erp", category: "Integraciones", name: "Integración con ERP", description: "Stock, precios, clientes y pedidos.", priceMonthly: 350, priceSetup: 1200 },
    { id: "int_ecommerce", category: "Integraciones", name: "Integración Ecommerce (Shopify/TiendaNube)", description: "Sync de catálogo, stock y órdenes.", priceMonthly: 350, priceSetup: 800 },

    // Automatizaciones
    { id: "auto_carrito_abandonado", category: "Automatizaciones", name: "Recupero de carritos abandonados", description: "Detección y recordatorios con link al carrito.", priceMonthly: 220, priceSetup: 350 },
    { id: "auto_cobro_pedidos_pendientes", category: "Automatizaciones", name: "Cobro de pedidos pendientes", description: "Recordatorios automáticos de pago.", priceMonthly: 240, priceSetup: 300 },
    { id: "auto_upsell_cross", category: "Automatizaciones", name: "Upselling & Cross-selling", description: "Sugerencias según historial y stock.", priceMonthly: 260, priceSetup: 380 },
    { id: "auto_repo_stock", category: "Automatizaciones", name: "Reposición y compra de stock", description: "Predice demanda y genera órdenes/alertas.", priceMonthly: 300, priceSetup: 500 },
    { id: "auto_bienvenida", category: "Automatizaciones", name: "Bienvenida post-registro", description: "Mensaje + catálogo tras registro.", priceMonthly: 120, priceSetup: 180 },
    { id: "auto_postventa", category: "Automatizaciones", name: "Seguimiento post-venta", description: "Tracking y solicitud de reseña.", priceMonthly: 180, priceSetup: 220 },
    { id: "auto_feedback_nps", category: "Automatizaciones", name: "Encuestas NPS/CSAT", description: "WhatsApp/Email + tablero y alertas.", priceMonthly: 210, priceSetup: 250 },
    { id: "auto_newsletter_ai", category: "Automatizaciones", name: "Newsletter + Promos con IA", description: "Campañas con recomendaciones.", priceMonthly: 230, priceSetup: 260 },
    { id: "auto_cat_sync", category: "Automatizaciones", name: "Ingesta/actualización de catálogo", description: "Carga de SKUs, imágenes y atributos.", priceMonthly: 200, priceSetup: 300 },
    { id: "auto_precio_dinamico", category: "Automatizaciones", name: "Optimización dinámica de precios", description: "Reglas/IA por demanda y margen.", priceMonthly: 320, priceSetup: 600 },

    // Add-ons
    { id: "addon_monitoring", category: "Add-ons", name: "Monitoreo & Alertas", description: "Salud de jobs y alertas críticas.", priceMonthly: 150, priceSetup: 150 },
    { id: "addon_training", category: "Add-ons", name: "Capacitación de equipo (mensual)", description: "Taller práctico para adopción de IA.", priceMonthly: 300, priceSetup: 0 },
    { id: "addon_content_ai", category: "Add-ons", name: "Contenido con IA", description: "Textos e imágenes para catálogo/redes.", priceMonthly: 220, priceSetup: 0 },
    { id: "addon_dashboard_bi", category: "Add-ons", name: "Dashboard BI", description: "Panel ejecutivo con KPIs y funnels.", priceMonthly: 260, priceSetup: 300 },
];

/* =====================
   Casos de uso (persisten)
   ===================== */
function CasesView() {
    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[#575fd5]">Casos de uso implementados</h2>
            <div className="grid md:grid-cols-2 gap-6">
                {useCasesData.map((c) => (
                    <motion.div key={c.id} whileHover={{ scale: 1.02 }} className="rounded-2xl border bg-white p-6 shadow">
                        <h3 className="text-lg font-semibold">{c.title}</h3>
                        <p className="text-xs text-neutral-500">Industria: {c.client}</p>
                        <p className="text-sm mt-3">{c.summary}</p>
                        {c.highlights && (
                            <ul className="mt-3 list-disc pl-5 text-sm text-neutral-700">
                                {c.highlights.map((h, i) => (
                                    <li key={i}>{h}</li>
                                ))}
                            </ul>
                        )}
                        {c.stack && (
                            <div className="mt-3 flex flex-wrap gap-2">
                                {c.stack.map((t) => (
                                    <span key={t} className="text-xs px-2 py-1 rounded-full bg-neutral-100 border">
                                        {t}
                                    </span>
                                ))}
                            </div>
                        )}
                    </motion.div>
                ))}
            </div>
        </div>
    );
}

const useCasesData = [
    { id: "case_catalogo", title: "Catálogo digital + Asistente distribuidores", client: "Ecommerce Mayorista", summary: "Catálogo online/WhatsApp actualizado y un asistente que permite consultar stock y encargar rápido.", highlights: ["Pedidos sin fricción", "Actualización diaria"], stack: ["WhatsApp API", "Chatbot", "Catálogo"] },
    { id: "case_productos", title: "Automatización venta – Productos nuevos", client: "Retail/Mayorista", summary: "Detecta clientes con afinidad y stock disponible y envía propuestas automáticas por WhatsApp.", highlights: ["No spam", "Sólo stock disponible"], stack: ["ERP", "WhatsApp", "Reglas"] },
    { id: "case_upsell", title: "Upselling & Cross-selling con IA", client: "Multirubro", summary: "IA que sugiere productos relacionados y prepara propuestas según últimas compras.", highlights: ["Aumenta ticket promedio", "Relevancia"], stack: ["IA", "Historial", "Catálogo"] },
    { id: "case_feedback", title: "Feedback de productos", client: "Distribuidores/Clientes finales", summary: "Encuestas periódicas para mejorar calidad, packaging y catálogo.", highlights: ["Señales accionables", "Mejora continua"], stack: ["Encuestas", "Dashboard"] },
    { id: "case_bienvenida", title: "Automatización de bienvenida", client: "Eventos/Web", summary: "Envía mensaje + catálogo al registrarse en web o evento.", highlights: ["Onboarding inmediato", "Segmentación"], stack: ["Webhook", "WhatsApp", "CRM"] },
    { id: "case_cobros", title: "Cobros anticipados", client: "Finanzas", summary: "Avisos automáticos sobre pagos a realizar.", highlights: ["Menor mora", "Seguimiento claro"], stack: ["WhatsApp", "Email"] },
    // { id: "case_alianzas", title: "Alianzas con marcas", client: "Growth", summary: "Diseños exclusivos y apertura a nuevos mercados.", highlights: ["Expansión", "Mayor awareness"], stack: ["Co-branding"] },
    { id: "case_botInterno", title: "Bot interno de información", client: "Operaciones", summary: "Consultas sobre stock, ventas, clientes, contable.", highlights: ["Menos búsqueda", "Consultas 24/7"], stack: ["RAG", "ERP", "DB"] },
    { id: "case_contenido", title: "Generación de contenido con IA", client: "Marketing B2B", summary: "Textos e imágenes optimizados para catálogos y redes.", highlights: ["Ahorro operativo", "Consistencia"], stack: ["Generative AI"] },
    { id: "case_stock", title: "Reposición de stock/materias primas", client: "Producción/Compras", summary: "Predice necesidades de insumos y genera alertas de compra.", highlights: ["Menos quiebres", "Mejor cashflow"], stack: ["Forecast", "Alertas"] },
    { id: "case_deuda", title: "Recordatorios de deuda automáticos", client: "Contable", summary: "WhatsApp/Email con seguimiento de pagos.", highlights: ["Menor mora", "Seguimiento eficiente"], stack: ["ERP", "WhatsApp"] },
];
